import React from "react";
import {
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  Radio,
} from "antd";
import locale from "antd/es/date-picker/locale/pt_BR";
import { RowCol } from "./EmployeeCreateForm";
import { getGenderOptions } from "./functions";
import { maxValueRule, formatCPF } from "../../../../utils/input";
import { useSettingValue } from "../../../../utils/Hooks";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Trans } from "common/components/MockedTrans.tsx";
import { getBrazilPhoneValidator } from "../../../../utils/phone";
import InputMask from "react-input-mask";

const useTranslation = () => {
  return { t: (text) => text };
};

export const EmployeeForm = ({
  form: employeeForm,
  onFinish,
  initialValues = undefined,
  onChange = undefined,
}) => {
  const { t } = useTranslation();
  const { options: genderOptions, defaultGender } = getGenderOptions();
  const isUsingCustomKey = useSettingValue(
    "T006_ALLOW_FIELD_SELECTION_AS_EMPLOYEE_KEY",
  );
  const customKey = useSettingValue("EMPLOYEE_CUSTOM_KEY");
  const enablePreferredChannel = useSettingValue(
    "T015_ENABLE_PREFERRED_CHANNEL",
  );

  const handleCpfChange = (e) => {
    const { value } = e.target;
    const formattedCpf = formatCPF(value);
    employeeForm.setFieldValue("cpf", formattedCpf);
    if (isUsingCustomKey && customKey === "cpf") {
      employeeForm.setFieldValue("employee_code", formattedCpf);
    }
  };

  const handleEmailChange = (e) => {
    if (isUsingCustomKey && customKey === "email") {
      const { value } = e.target;
      employeeForm.setFieldValue("employee_code", value);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const { value } = e.target;
    employeeForm.setFieldValue("phone_number", value);
  };

  // should be enabled only on edition mode
  const handleChange = (e) => {
    onChange && onChange(e);
  };

  const preferred_channel = Form.useWatch("preferred_channel", employeeForm);

  const phoneValue = Form.useWatch("phone_number", employeeForm);

  return (
    <Form
      initialValues={initialValues}
      form={employeeForm}
      onFinish={onFinish}
      labelAlign="left"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 12 }}
      onChange={handleChange}
    >
      <Row>
        <Col span={12}>
          <Form.Item
            label={t("Nome")}
            name="first_name"
            wrapperCol={{ span: 12, offset: 6 }}
            rules={[
              { required: true, message: t("Por favor insira o nome.") },
              maxValueRule(100),
            ]}
          >
            <Input placeholder={t("Nome")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label=""
            name="last_name"
            wrapperCol={{ span: 11, offset: 1 }}
            rules={[
              { required: true, message: t("Por favor insira o sobrenome.") },
              maxValueRule(100),
            ]}
          >
            <Input placeholder={t("Sobrenome")} />
          </Form.Item>
        </Col>
      </Row>
      <RowCol>
        <Form.Item
          label={
            <span>
              <Trans>Nome de usuário</Trans>{" "}
              <Tooltip
                title={t(
                  "Este será o nome de usuário utilizado pelo funcionário para acessar nossos sistemas.",
                )}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </span>
          }
          name="username"
          rules={[
            {
              required: true,
              message: t("Por favor insira o nome de usuário."),
            },
            maxValueRule(100),
          ]}
        >
          <Input placeholder={t("Nome de usuário")} />
        </Form.Item>
      </RowCol>
      <RowCol>
        <Form.Item
          label={t("Código do funcionário")}
          name="employee_code"
          rules={[
            { required: true, message: t("Por favor insira o código") },
            maxValueRule(254),
          ]}
        >
          <Input
            placeholder={t("Código")}
            disabled={isUsingCustomKey && customKey !== "employee_code"}
          />
        </Form.Item>
      </RowCol>
      <RowCol>
        <Form.Item
          label={t("CPF")}
          name="cpf"
          rules={
            isUsingCustomKey && customKey === "cpf"
              ? [
                  { required: true, message: t("Por favor insira o CPF.") },
                  maxValueRule(14),
                ]
              : [maxValueRule(14)]
          }
        >
          <Input placeholder="000.000.000-00" onChange={handleCpfChange} />
        </Form.Item>
      </RowCol>
      <RowCol>
        <Form.Item label={t("Gênero")} name="gender">
          <Select
            placeholder={t("Gênero")}
            defaultValue={defaultGender}
            options={genderOptions}
            onChange={(_, option) =>
              handleChange({
                target: { id: "gender", option },
                customDiffers: (initialGender, newGender) => {
                  return initialGender.value !== option.value;
                },
              })
            }
          />
        </Form.Item>
      </RowCol>
      <RowCol>
        <Form.Item label={t("Data de nascimento")} name="birth_date">
          <DatePicker
            locale={locale}
            placeholder={t("Selecione uma data")}
            format={"DD/MM/YYYY"}
            style={{ width: "100%" }}
            onChange={(value) => {
              handleChange({
                target: {
                  id: "birth_date",
                  value: value,
                },
                customDiffers: (initialMoment, newMoment) => {
                  if (initialMoment == null) return newMoment != null;
                  if (newMoment == null) return true;
                  return initialMoment.format() !== newMoment.format();
                },
              });
            }}
          />
        </Form.Item>
      </RowCol>
      <div style={{ height: 42 }} />
      <RowCol>
        <Form.Item
          label={t("E-mail")}
          name="email"
          dependencies={enablePreferredChannel ? ["preferred_channel"] : []}
          required={
            !enablePreferredChannel ||
            (enablePreferredChannel && preferred_channel === "email")
          }
          rules={[
            {
              type: "email",
              message: t("Por favor insira um e-mail válido."),
            },
            {
              required:
                !enablePreferredChannel ||
                (enablePreferredChannel && preferred_channel === "email"),
              message: t("Por favor insira um e-mail."),
            },
            maxValueRule(254),
          ]}
          onChange={handleEmailChange}
        >
          <Input placeholder={t("E-mail")} />
        </Form.Item>
      </RowCol>
      {enablePreferredChannel && (
        <>
          <RowCol>
            <Form.Item
              label={t("Telefone celular")}
              name="phone_number"
              dependencies={["preferred_channel"]}
              required={preferred_channel === "phone_number"}
              rules={[
                {
                  required: preferred_channel === "phone_number",
                  message: t("Por favor insira um número de telefone celular."),
                },
                maxValueRule(20),
                getBrazilPhoneValidator(t),
              ]}
            >
              <InputMask
                mask="(99) 99999-9999"
                value={phoneValue}
                onChange={handlePhoneNumberChange}
                maskChar={null}
              >
                {(inputProps) => (
                  <Input placeholder={t("Telefone celular")} {...inputProps} />
                )}
              </InputMask>
            </Form.Item>
          </RowCol>

          <RowCol>
            <Form.Item
              colon={false}
              label={
                <span>
                  {t("Canal preferencial")}:{" "}
                  <Tooltip
                    title={
                      <span>
                        {t(
                          "Defina o canal utilizado para criação e redefinição de senha.",
                        )}
                        <br />
                        {t(
                          "Quando o funcionário não possui e-mail corporativo, o telefone celular é a opção mais indicada.",
                        )}
                      </span>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              }
              name="preferred_channel"
              rules={[
                {
                  required: true,
                  message: t("Por favor selecione o canal preferencial."),
                },
              ]}
            >
              <Radio.Group
                onChange={(e) =>
                  handleChange({
                    target: { id: "preferred_channel", value: e.target.value },
                  })
                }
              >
                <Radio value="email">{t("E-mail")}</Radio>
                <Radio value="phone_number">{t("Telefone celular")}</Radio>
              </Radio.Group>
            </Form.Item>
          </RowCol>
        </>
      )}

      <div style={{ height: 42 }} />

      <RowCol>
        <Form.Item
          label={t("Local de trabalho")}
          name="work_city"
          rules={[maxValueRule(64)]}
        >
          <Input placeholder={t("Local")} />
        </Form.Item>
      </RowCol>
      <RowCol>
        <Form.Item
          label={t("Como conheceu a empresa")}
          name="company_referal"
          rules={[maxValueRule(256)]}
        >
          <Input.TextArea placeholder={t("Descreva")} />
        </Form.Item>
      </RowCol>
    </Form>
  );
};
